<template>
  <div class="pd-page">
    <!-- Loading -->
    <div v-if="loading" class="container py-4">
      <div class="skel skel-cover"></div>
      <div class="skel skel-line w-50 mt-3"></div>
      <div class="skel skel-line w-80 mt-2"></div>
    </div>

    <!-- Error / not found -->
    <div v-else-if="!product" class="pd-state">
      <i class="pi pi-exclamation-triangle"></i>
      <p>لم يتم العثور على هذا المنتج.</p>
      <router-link :to="backLink" class="pd-back-btn">رجوع</router-link>
    </div>

    <template v-else>
      <!-- Cover -->
      <div class="pd-cover" :style="coverStyle">
        <div class="pd-cover__overlay"></div>
        <div class="container pd-cover__content">
          <div class="pd-badges">
            <span class="pd-kind">{{ kind === 'course' ? 'كورس' : 'سنة دراسية' }}</span>
            <span v-if="isOwnYear" class="pd-badge pd-badge--own"><i class="pi pi-star-fill"></i> سنتك الدراسية</span>
            <span class="pd-badge" :class="statusBadge.tone">{{ statusBadge.label }}</span>
          </div>
          <h1 class="pd-title">{{ title }}</h1>
          <p v-if="shortDescription" class="pd-short-desc">{{ shortDescription }}</p>
          <p v-if="product.teacher" class="pd-teacher"><i class="pi pi-user"></i> {{ product.teacher }}</p>
          <span class="pd-price-badge">{{ priceBadgeText }}</span>
        </div>
      </div>

      <div class="container py-4">
        <!-- ══ State B: Course Content Mode — the student already has access ══ -->
        <template v-if="contentMode">
          <!-- Real subscription details (Feature: subscription visibility) — only
               rendered when there's an actual paid subscription row; free/center
               access has no subscription to show details for. -->
          <div v-if="subscription" class="pd-sub-card" :class="{ 'pd-sub-card--expiring': subExpiringSoon || subscription.expiry_state === 'expires_today' }">
            <h3 class="pd-h3">الاشتراك الحالي</h3>
            <div class="pd-sub-status">{{ subStatusLine }}</div>
            <div class="pd-sub-grid">
              <div><span>الخطة</span><b>{{ planLabel(subscription.plan_type) }}</b></div>
              <div><span>تاريخ بداية الاشتراك</span><b>{{ date(subscription.starts_at) }}</b></div>
              <div><span>تاريخ انتهاء الاشتراك</span><b>{{ isLifetimeSub ? 'مدى الحياة' : date(subscription.expires_at) }}</b></div>
              <div><span>المتبقي</span><b>{{ isLifetimeSub ? 'مدى الحياة' : remainingPhrase }}</b></div>
            </div>
            <button v-if="subscription.expiry_state === 'expired' || subExpiringSoon || subscription.expiry_state === 'expires_today'" class="pd-subscribe" @click="subscribe">
              <i class="pi pi-refresh"></i> تجديد الاشتراك
            </button>
          </div>

          <div class="pd-block">
            <h3 class="pd-h3">عن {{ kind === 'course' ? 'الكورس' : 'السنة الدراسية' }}</h3>
            <p class="pd-desc">{{ product.description || 'لا يوجد وصف متاح.' }}</p>
          </div>

          <div class="pd-block">
            <h3 class="pd-h3">محتوى {{ kind === 'course' ? 'الكورس' : 'السنة الدراسية' }}</h3>
            <CourseContentBrowser
              :course-id="kind === 'course' ? id : null"
              :academic-year-id="kind === 'academic_year' ? id : null"
            />
          </div>

          <div class="pd-block" v-if="examsLoading || courseExams.length">
            <h3 class="pd-h3">الامتحانات</h3>
            <p v-if="examsLoading" class="pd-noplans">جاري تحميل الامتحانات...</p>
            <div v-else class="pd-exams-grid">
              <router-link
                v-for="e in courseExams" :key="e.id"
                :to="`/exam/${e.id}?type=${e.duration_required ? 'online' : 'homework'}`"
                class="pd-exam-card"
              >
                <span class="pd-exam-icon"><i class="pi" :class="e.duration_required ? 'pi-file-edit' : 'pi-home'"></i></span>
                <span class="pd-exam-info">
                  <span class="pd-exam-title">{{ e.title }}</span>
                  <span class="pd-exam-kind">{{ e.duration_required ? 'امتحان أونلاين' : 'واجب منزلي' }}</span>
                </span>
                <span v-if="e.taken" class="pd-exam-taken"><i class="pi pi-check-circle"></i> تم التأدية</span>
                <i v-else class="pi pi-angle-left pd-exam-arrow"></i>
              </router-link>
            </div>
          </div>
        </template>

        <!-- ══ State A: marketing + subscription plans ══ -->
        <div class="row g-4" v-else>
          <!-- Right (RTL): full description + features + FAQ -->
          <div class="col-lg-7">
            <div class="pd-block">
              <h3 class="pd-h3">عن {{ kind === 'course' ? 'الكورس' : 'السنة الدراسية' }}</h3>
              <p class="pd-desc">{{ product.description || 'لا يوجد وصف متاح.' }}</p>
            </div>

            <div class="pd-block" v-if="stats.length">
              <h3 class="pd-h3">إحصائيات</h3>
              <div class="pd-stats">
                <div v-for="s in stats" :key="s.label" class="pd-stat">
                  <div class="pd-stat__value">{{ s.value }}</div>
                  <div class="pd-stat__label">{{ s.label }}</div>
                </div>
              </div>
            </div>

            <div class="pd-block">
              <h3 class="pd-h3">ماذا ستحصل عليه</h3>
              <ul class="pd-features">
                <li v-for="f in features" :key="f"><i class="pi pi-check-circle"></i> {{ f }}</li>
              </ul>
            </div>

            <div class="pd-block">
              <h3 class="pd-h3">الأسئلة الشائعة</h3>
              <div class="pd-faq">
                <p class="pd-faq__empty"><i class="pi pi-info-circle"></i> لا توجد أسئلة شائعة مضافة بعد لهذا المنتج.</p>
              </div>
            </div>
          </div>

          <!-- Left (RTL): subscription card -->
          <div class="col-lg-5">
            <div class="pd-plans-card">
              <h3 class="pd-h3">{{ isFree ? 'الوصول' : 'الخطط المتاحة' }}</h3>

              <!-- Free product: no subscription required -->
              <template v-if="isFree">
                <div class="pd-free-badge"><i class="pi pi-gift"></i> مجاني</div>
                <button class="pd-subscribe" @click="primaryAction">
                  <i class="pi pi-arrow-left"></i> ابدأ الآن
                </button>
              </template>

              <!-- Priced plans -->
              <template v-else>
                <div v-if="plans.length" class="pd-plans">
                  <button v-for="p in plans" :key="p.type" class="pd-plan"
                          :class="{ active: selectedPlan === p.type }" @click="selectedPlan = p.type">
                    <span class="pd-plan__name">{{ p.label }}</span>
                    <span class="pd-plan__price">{{ money(p.price) }}</span>
                  </button>
                </div>
                <p v-else class="pd-noplans">لا توجد خطط متاحة حالياً.</p>

                <button class="pd-subscribe" :class="{ 'pd-subscribe--access': hasAccess }"
                        :disabled="!hasAccess && (!product.accepts_subscriptions || !plans.length)"
                        @click="primaryAction">
                  <i class="pi" :class="hasAccess ? 'pi-sign-in' : 'pi-bolt'"></i>
                  {{ ctaLabel }}
                </button>
                <p v-if="!hasAccess && !product.accepts_subscriptions" class="pd-hint">الاشتراك غير مفعّل لهذا المنتج حالياً.</p>
              </template>
            </div>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import { subscriptionService } from '@/services/subscriptionService.js';
import { productPlans, formatMoney, planLabel, formatDate, daysRemainingPhrase, isExpiringSoon, expiryStateBadge } from '@/composables/subscriptionMeta.js';
import CourseContentBrowser from '@/components/course/CourseContentBrowser.vue';

export default {
  name: 'ProductDetails',
  components: { CourseContentBrowser },
  props: {
    kind: { type: String, default: 'course' },
  },
  data() {
    return { product: null, loading: true, selectedPlan: null, courseExams: [], examsLoading: false };
  },
  computed: {
    id() { return this.$route.params.id; },
    backLink() { return this.kind === 'course' ? '/courses' : '/academic-years'; },
    title() { return this.kind === 'course' ? this.product?.title : this.product?.name; },
    plans() { return productPlans(this.product); },
    coverStyle() {
      const img = this.product?.cover_image_link || this.product?.image_link;
      if (img) return { backgroundImage: `url(${img})` };
      return { background: `linear-gradient(135deg, ${this.product?.color || '#5B5FEF'}, #7c4dff)` };
    },
    // Hero shows a short excerpt; the full text lives in the "عن..." block below.
    // Course and AcademicYear both only store a single `description` field, so the
    // "short description" is a display-only derivation, not a separate column.
    shortDescription() {
      const text = this.product?.description || '';
      if (!text) return '';
      return text.length > 140 ? text.slice(0, 140).trim() + '…' : text;
    },
    isFree() { return !this.product?.is_subscription_enabled; },
    hasAccess() { return !!this.product?.has_access; },
    auth() { return this.$store.getters.isAuthenticated; },
    // "Course Content Mode" (Feature: hide plans after subscription) — the backend
    // already tells us whether this student owns the product via has_access; we
    // just switch the page's layout on that existing flag instead of re-deriving
    // access rules on the frontend.
    contentMode() { return this.auth && this.hasAccess; },
    isOwnYear() { return this.kind === 'academic_year' && !!this.product?.is_students_year; },
    subscription() { return this.product?.subscription || null; },
    isLifetimeSub() {
      return !!this.subscription && (this.subscription.plan_type === 'lifetime' || !this.subscription.expires_at);
    },
    // Driven by the backend-computed expiry_state (Subscription::expiryState()) —
    // see subscriptionMeta.js and MySubscriptions.vue for why this must never be
    // re-derived from raw status/days_remaining on the frontend.
    subExpiringSoon() { return isExpiringSoon(this.subscription); },
    remainingPhrase() {
      if (!this.subscription) return '—';
      if (this.subscription.expiry_state === 'expired') return `انتهى في ${this.date(this.subscription.expires_at)}`;
      if (this.subscription.expiry_state === 'expires_today') return 'اليوم';
      return daysRemainingPhrase(this.subscription.days_remaining);
    },
    subStatusLine() {
      if (!this.subscription) return '';
      const badge = expiryStateBadge(this.subscription);
      if (badge) return badge.text;
      return '🟢 الاشتراك نشط';
    },
    statusBadge() {
      if (this.hasAccess) return { label: 'أنت مشترك', tone: 'pd-badge--success' };
      if (this.isFree) return { label: 'متاح مجاناً', tone: 'pd-badge--success' };
      if (this.product?.accepts_subscriptions) return { label: 'متاح للاشتراك', tone: 'pd-badge--info' };
      return { label: 'الاشتراك غير متاح حالياً', tone: 'pd-badge--muted' };
    },
    priceBadgeText() {
      if (this.isFree) return 'مجاني';
      const min = this.plans.length ? Math.min(...this.plans.map(p => p.price)) : null;
      return min !== null ? `يبدأ من ${this.money(min)}` : 'السعر غير محدد بعد';
    },
    ctaLabel() {
      if (this.hasAccess) return 'ادخل الآن';
      return 'اشترك الآن';
    },
    stats() {
      // Only surface stats the backend actually provides.
      const s = [];
      if (this.plans.length) s.push({ label: 'خطط متاحة', value: this.plans.length });
      return s;
    },
    features() {
      return this.kind === 'course'
        ? ['وصول كامل لمحتوى الكورس', 'فيديوهات وملفات', 'امتحانات وواجبات', 'بنك أسئلة', 'متابعة مستمرة']
        : ['وصول كامل لمحتوى السنة الدراسية', 'الحصص والوحدات', 'امتحانات وواجبات', 'بنك أسئلة'];
    },
  },
  methods: {
    money: formatMoney,
    planLabel,
    date: formatDate,
    async load() {
      this.loading = true;
      try {
        if (this.kind === 'course') {
          this.product = await subscriptionService.course(this.id);
        } else {
          this.product = await subscriptionService.academicYear(this.id);
        }
        if (this.plans.length) this.selectedPlan = this.plans[0].type;
        if (this.contentMode) await this.loadExams();
      } catch {
        this.product = null;
      } finally {
        this.loading = false;
      }
    },
    /** Free product, already-subscribed, or fresh subscribe — routes to the right place. */
    primaryAction() {
      if (this.hasAccess || this.isFree) {
        this.$router.push(this.kind === 'academic_year' ? '/contents' : '/my-subscriptions');
        return;
      }
      this.subscribe();
    },
    subscribe() {
      this.$router.push({ path: '/subscribe', query: { type: this.kind, id: this.id, plan: this.selectedPlan || undefined } });
    },
    /** Course/academic-year scoped exam list for Course Content Mode. */
    async loadExams() {
      this.examsLoading = true;
      try {
        const params = this.kind === 'course' ? { course_id: this.id } : { academic_year_id: this.id };
        this.courseExams = await subscriptionService.examsWeb(params);
      } catch {
        this.courseExams = [];
      } finally {
        this.examsLoading = false;
      }
    },
  },
  mounted() { this.load(); },
};
</script>

<style scoped>
.pd-page { min-height: 60vh; padding-bottom: 5rem; }
.pd-cover {
  position: relative; min-height: 240px;
  background-size: cover; background-position: center;
  display: flex; align-items: flex-end;
}
.pd-cover__overlay {
  position: absolute; inset: 0;
  background: linear-gradient(to top, rgba(15,23,42,.82), rgba(15,23,42,.25));
}
.pd-cover__content { position: relative; padding: 1.5rem 0 1.75rem; color: #fff; }
.pd-badges { display: flex; flex-wrap: wrap; align-items: center; gap: .5rem; margin-bottom: .6rem; }
.pd-kind {
  display: inline-block; background: rgba(255,255,255,.18); backdrop-filter: blur(4px);
  padding: .25rem .75rem; border-radius: 99px; font-size: .75rem; font-weight: 700;
}
.pd-badge {
  display: inline-flex; align-items: center; gap: .3rem;
  padding: .25rem .75rem; border-radius: 99px; font-size: .75rem; font-weight: 700;
  background: rgba(255,255,255,.18); backdrop-filter: blur(4px);
}
.pd-badge--own { background: rgba(245,158,11,.9); color: #1f2937; }
.pd-badge--success { background: rgba(34,197,94,.9); }
.pd-badge--info { background: rgba(91,95,239,.85); }
.pd-badge--muted { background: rgba(255,255,255,.14); opacity: .85; }
.pd-title { font-size: clamp(1.5rem, 4vw, 2.2rem); font-weight: 800; margin: 0; }
.pd-short-desc { margin: .5rem 0 0; font-size: .92rem; opacity: .92; max-width: 640px; line-height: 1.7; }
.pd-teacher { margin: .5rem 0 0; font-weight: 700; opacity: .95; }
.pd-price-badge {
  display: inline-block; margin-top: .9rem; background: #fff; color: #4338ca;
  padding: .4rem 1rem; border-radius: 10px; font-weight: 800; font-size: .95rem;
}
.pd-sub-card {
  background: #fff; border: 1px solid rgba(91,95,239,.12); border-radius: 16px;
  padding: 1.2rem; margin-bottom: 1.75rem;
}
.pd-sub-card--expiring { border-color: rgba(245,158,11,.35); background: #fffaf0; }
.pd-sub-status { font-weight: 700; font-size: .92rem; margin-bottom: .9rem; }
.pd-sub-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: .8rem; margin-bottom: 1rem; }
.pd-sub-grid span { display: block; font-size: .72rem; color: #94a3b8; }
.pd-sub-grid b { font-size: .88rem; color: #374151; }
.pd-block { margin-bottom: 1.75rem; }
.pd-h3 { font-size: 1.05rem; font-weight: 800; color: #111827; margin-bottom: .75rem; }
.pd-desc { color: #4b5563; line-height: 1.9; font-size: .95rem; }
.pd-stats { display: flex; gap: 1rem; flex-wrap: wrap; }
.pd-stat { background: #f8fafc; border: 1px solid #eef2f7; border-radius: 14px; padding: 1rem 1.4rem; text-align: center; }
.pd-stat__value { font-size: 1.5rem; font-weight: 800; color: #5B5FEF; }
.pd-stat__label { font-size: .8rem; color: #6B7280; }
.pd-features { list-style: none; padding: 0; margin: 0; display: grid; gap: .6rem; }
.pd-features li { display: flex; align-items: center; gap: .5rem; color: #374151; font-size: .92rem; }
.pd-features .pi-check-circle { color: #22c55e; }
.pd-plans-card {
  background: #fff; border: 1px solid rgba(91,95,239,.12); border-radius: 18px;
  padding: 1.4rem; position: sticky; top: 1rem;
  box-shadow: 0 8px 24px rgba(91,95,239,.08);
}
.pd-plans { display: grid; gap: .6rem; margin-bottom: 1rem; }
.pd-plan {
  display: flex; justify-content: space-between; align-items: center;
  border: 1.5px solid #e2e8f0; background: #fff; border-radius: 12px;
  padding: .8rem 1rem; cursor: pointer; transition: all .15s ease;
}
.pd-plan.active { border-color: #5B5FEF; background: rgba(91,95,239,.05); }
.pd-plan__name { font-weight: 700; color: #374151; }
.pd-plan__price { font-weight: 800; color: #5B5FEF; }
.pd-noplans { color: #94a3b8; font-size: .88rem; }
.pd-exams-grid { display: grid; gap: .75rem; grid-template-columns: repeat(auto-fill, minmax(260px, 1fr)); }
.pd-exam-card {
  display: flex; align-items: center; gap: .75rem;
  background: #fff; border: 1.5px solid #e2e8f0; border-radius: 14px;
  padding: .9rem 1rem; text-decoration: none; color: inherit;
  transition: all .15s ease;
}
.pd-exam-card:hover { border-color: #5B5FEF; box-shadow: 0 8px 20px rgba(91,95,239,.12); transform: translateY(-1px); }
.pd-exam-icon {
  width: 42px; height: 42px; border-radius: 12px; flex-shrink: 0;
  display: flex; align-items: center; justify-content: center;
  background: rgba(91,95,239,.08); color: #5B5FEF; font-size: 1.1rem;
}
.pd-exam-info { flex: 1; display: flex; flex-direction: column; gap: .15rem; min-width: 0; }
.pd-exam-title { font-weight: 700; color: #111827; font-size: .95rem; }
.pd-exam-kind { font-size: .78rem; color: #6B7280; }
.pd-exam-taken { display: inline-flex; align-items: center; gap: .3rem; color: #16a34a; font-size: .8rem; font-weight: 700; white-space: nowrap; }
.pd-exam-arrow { color: #D1D5DB; }
.pd-free-badge {
  display: flex; align-items: center; justify-content: center; gap: .5rem;
  background: rgba(34,197,94,.1); color: #16a34a; border: 1.5px solid rgba(34,197,94,.3);
  border-radius: 12px; padding: .9rem; font-weight: 800; font-size: 1.05rem; margin-bottom: 1rem;
}
.pd-subscribe {
  width: 100%; background: linear-gradient(90deg, #5B5FEF, #7c4dff); color: #fff;
  border: none; border-radius: 12px; padding: .8rem; font-weight: 800; font-size: 1rem; cursor: pointer;
  display: inline-flex; align-items: center; justify-content: center; gap: .5rem;
}
.pd-subscribe--access { background: linear-gradient(90deg, #22c55e, #16a34a); }
.pd-subscribe:disabled { background: #cbd5e1; cursor: not-allowed; }
.pd-hint { font-size: .78rem; color: #94a3b8; text-align: center; margin: .6rem 0 0; }
.pd-faq__empty {
  color: #94a3b8; font-size: .88rem; background: #f8fafc; border: 1px dashed #e2e8f0;
  border-radius: 12px; padding: .9rem 1rem; display: flex; align-items: center; gap: .5rem; margin: 0;
}
.pd-state { text-align: center; padding: 4rem 1rem; color: #94a3b8; }
.pd-state .pi { font-size: 2.6rem; display: block; margin-bottom: .8rem; }
.pd-back-btn { display: inline-block; margin-top: .5rem; background: #5B5FEF; color: #fff; border-radius: 10px; padding: .5rem 1.2rem; font-weight: 700; text-decoration: none; }
.skel { background: #e9edf5; border-radius: 10px; position: relative; overflow: hidden; }
.skel::after { content: ''; position: absolute; inset: 0; transform: translateX(-100%); background: linear-gradient(90deg, transparent, rgba(255,255,255,.6), transparent); animation: sh 1.3s infinite; }
@keyframes sh { 100% { transform: translateX(100%); } }
.skel-cover { height: 200px; } .skel-line { height: 14px; } .w-50 { width: 50%; } .w-80 { width: 80%; }
</style>
